import {
  type Currency,
  STOREFRONT_FONT_CATEGORIES,
  STOREFRONT_FONTS,
  safeThemeFontFamily,
  svgIconDataUri,
  TENANT_ASSET_FILE_ACCEPT,
  toLoadableFontStack,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { CurrencyIcon } from "@loot-os/ui/components/currency-icon";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { cn } from "@loot-os/ui/lib/utils";
import { X } from "lucide-react";
import { type ReactNode, useState } from "react";

import { DEFAULT_RADIUS_PX, MAX_RADIUS_PX, radiusToPx } from "../lib/storefront";
import { useSvgFilePick } from "../lib/useSvgFilePick";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * Corner radius as a slider in px. The field used to take raw CSS, which made
 * a unitless "40" the obvious thing to type — invalid as a length, so the
 * storefront dropped it and the corners never moved off the default. Writing
 * the unit here means every value on the track lands.
 */
export function RadiusField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const stored = radiusToPx(value);
  const current = stored ?? DEFAULT_RADIUS_PX;
  const set = (size: number) =>
    onChange(`${Math.min(MAX_RADIUS_PX, Math.max(0, Math.round(size)))}px`);
  return (
    <div className="grid gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor="theme-radius">Corner radius</Label>
        <code className="font-mono text-xs text-muted-foreground">--radius</code>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="range"
          aria-label="Corner radius slider"
          min={0}
          max={MAX_RADIUS_PX}
          step={1}
          value={current}
          onChange={(e) => set(Number(e.target.value))}
          className="h-9 min-w-0 flex-1 cursor-pointer accent-primary"
        />
        <Input
          id="theme-radius"
          type="number"
          min={0}
          max={MAX_RADIUS_PX}
          value={current}
          onChange={(e) => {
            if (e.target.value !== "") set(Number(e.target.value));
          }}
          className="w-20 shrink-0"
        />
        <span className="text-sm text-muted-foreground">px</span>
        {/* The slider can't express "unset", so falling back to the default needs its own control. */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={value === "" ? "invisible shrink-0" : "shrink-0"}
          aria-label="Reset corner radius to default"
          disabled={value === ""}
          onClick={() => onChange("")}
        >
          <X />
        </Button>
      </div>
      {/* Sized past 2 × MAX_RADIUS_PX in both axes: a smaller box clamps its own
          corners at half its height, so the top of the track would render as one
          unchanging pill and the slider would look stuck. */}
      <div className="flex items-center gap-3 pt-1">
        <span
          aria-hidden
          className="h-32 w-40 shrink-0 border border-input bg-muted"
          style={{ borderRadius: `${current}px` }}
        />
        <span className="text-xs text-muted-foreground">
          {stored === null ? `Default — ${DEFAULT_RADIUS_PX}px` : `Preview — ${current}px`}
        </span>
      </div>
    </div>
  );
}

/**
 * Hex text input paired with a native color picker. An empty value means
 * "inherit the default": the field shows the default as its placeholder and the
 * swatch previews it, but nothing is stored until the tenant types or picks a color.
 */
export function ColorField({
  id,
  label,
  hint,
  fallback,
  placeholder = fallback,
  value,
  onChange,
}: {
  id: string;
  label: string;
  /** The CSS variable this field writes, shown so the mapping isn't a guess. */
  hint: string;
  /** Default palette value, previewed while the field is unset; "" when there is none to show. */
  fallback: string;
  /** What the empty field says; the fallback unless it can't show one (a derived color). */
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const effective = value.trim() === "" ? fallback : value;
  // Nothing to show (a derived color left unset): an empty swatch, never the picker's black.
  const empty = effective.trim() === "";
  return (
    <div className="grid gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        <code className="font-mono text-xs text-muted-foreground">{hint}</code>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={`${label} picker`}
          value={HEX_RE.test(effective) ? effective : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "size-9 shrink-0 cursor-pointer rounded-md border border-input bg-transparent p-1",
            empty &&
              "border-dashed [&::-moz-color-swatch]:opacity-0 [&::-webkit-color-swatch]:opacity-0",
          )}
        />
        <Input
          id={id}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={value.trim() === "" ? "text-muted-foreground" : undefined}
        />
        {/* The picker can't express "unset", so reverting to the default needs its own
            control. Hidden (not removed) while unset so the row's width never shifts. */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={value === "" ? "invisible shrink-0" : "shrink-0"}
          aria-label={`Reset ${label} to default`}
          disabled={value === ""}
          onClick={() => onChange("")}
        >
          <X />
        </Button>
      </div>
    </div>
  );
}

/**
 * One pick from a short fixed list, as a segmented control. The options are native radio inputs,
 * so the arrow keys move the pick and Tab reaches only the picked option.
 */
export function ChoiceField<V extends string | number>({
  id,
  label,
  options,
  value,
  onChange,
  disabled,
  hideLabel,
}: {
  id: string;
  label: string;
  /** The label is for screen readers only, when the row's own title already says it. */
  hideLabel?: boolean;
  options: readonly { value: V; label: string }[];
  value: V;
  onChange: (value: V) => void;
  disabled?: boolean;
}) {
  return (
    <fieldset className="grid min-w-0 content-start gap-2" disabled={disabled}>
      <legend className={hideLabel ? "sr-only" : "mb-2 text-sm font-medium"}>{label}</legend>
      <div className="flex w-fit max-w-full flex-wrap gap-0.5 rounded-lg border bg-muted/50 p-0.5">
        {options.map((option) => (
          <label
            key={String(option.value)}
            className={cn(
              "cursor-pointer rounded-md px-3 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground",
              "has-checked:bg-background has-checked:font-medium has-checked:text-foreground has-checked:shadow-sm",
              "has-focus-visible:ring-2 has-focus-visible:ring-ring has-disabled:cursor-not-allowed has-disabled:opacity-50",
            )}
          >
            <input
              type="radio"
              name={id}
              value={String(option.value)}
              checked={option.value === value}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            {option.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** The picker's two entries that are not catalog fonts: the default, and a typed stack. */
const FONT_DEFAULT = "__default";
const FONT_CUSTOM = "__custom";

/** The catalog as the font picker's `items`, so its trigger shows a font's name, not its id. */
const FONT_ITEMS: Record<string, string> = {
  [FONT_DEFAULT]: "Default",
  ...Object.fromEntries(STOREFRONT_FONTS.map((font) => [font.id, font.name])),
  [FONT_CUSTOM]: "Custom…",
};

/**
 * A font, picked from the catalog: picking one writes `Name, fallback`, which the storefront
 * loads from its own origin (`STOREFRONT_FONTS`). Default clears it (`defaultLabel` says what
 * that means), and Custom opens a field for a stack typed by hand, kept as written, with a hint
 * of what the storefront does with it, including a stack its sanitizer drops. A stored stack the
 * catalog doesn't name opens as Custom.
 */
export function FontField({
  id,
  label,
  defaultLabel,
  placeholder,
  value,
  onChange,
}: {
  id: string;
  label: string;
  /** What Default draws ("The shopper's system font"). */
  defaultLabel: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [typing, setTyping] = useState(false);
  const blank = value.trim() === "";
  const safe = safeThemeFontFamily(value);
  const catalog = safe ? toLoadableFontStack(safe).font : null;
  // A catalog pick is stored as `Name, fallback`, and a reference theme names the font alone
  // (`Inter`); anything else typed by hand is custom.
  const picked =
    catalog &&
    (value.trim() === `${catalog.name}, ${catalog.fallback}` || value.trim() === catalog.name)
      ? catalog
      : null;
  const custom = typing || (!blank && !picked);
  const rejected = !blank && !safe;
  const selected = custom ? FONT_CUSTOM : (picked?.id ?? FONT_DEFAULT);
  const hint = rejected
    ? "The storefront ignores this: use font names separated by commas, up to 100 characters."
    : catalog
      ? `The storefront loads ${catalog.name}.`
      : "Not in the list: shows only on devices that already have this font.";

  return (
    <div className="grid content-start gap-2 @lg:grid-cols-[9rem_minmax(0,1fr)] @lg:items-center">
      <Label htmlFor={id}>{label}</Label>
      <Select
        items={FONT_ITEMS}
        value={selected}
        onValueChange={(next) => {
          if (next === FONT_CUSTOM) {
            setTyping(true);
            return;
          }
          setTyping(false);
          if (next === FONT_DEFAULT) {
            onChange("");
            return;
          }
          const font = STOREFRONT_FONTS.find((entry) => entry.id === next);
          if (font) onChange(`${font.name}, ${font.fallback}`);
        }}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={FONT_DEFAULT}>
            Default <span className="text-muted-foreground">· {defaultLabel}</span>
          </SelectItem>
          {STOREFRONT_FONT_CATEGORIES.map((category) => (
            <SelectGroup key={category.id}>
              <SelectLabel>{category.label}</SelectLabel>
              {STOREFRONT_FONTS.filter((entry) => entry.category === category.id).map((entry) => (
                <SelectItem key={entry.id} value={entry.id}>
                  {entry.name}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
          <SelectItem value={FONT_CUSTOM}>Custom…</SelectItem>
        </SelectContent>
      </Select>
      {custom && (
        <div className="grid gap-1.5 @lg:col-start-2">
          <Input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            aria-label={`${label}: font stack`}
            aria-invalid={rejected || undefined}
            aria-describedby={`${id}-hint`}
          />
          {!blank && (
            <p
              id={`${id}-hint`}
              className={cn("text-xs", rejected ? "text-destructive" : "text-muted-foreground")}
            >
              {hint}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** A color chip for the hub's summary cards — no interaction, just the value. */
export function Swatch({ color, title }: { color: string; title: string }) {
  return (
    <span
      aria-hidden
      title={title}
      className="size-6 shrink-0 rounded-full border border-background ring-1 ring-border"
      style={{ backgroundColor: color }}
    />
  );
}

/**
 * Upload/clear one of the storefront's SVG icons — the currency icon, the chat
 * launcher. Validation runs here for instant feedback and again in the
 * mutation, which is the authority — this copy is a convenience. The preview
 * uses the same `<img>` data-URI path the storefront does, so what staff see is
 * exactly what shoppers get; inlining the markup instead would turn tenant
 * input into XSS (see api-contract's svgIcon.ts).
 */
export function SvgIconField({
  id,
  label,
  value,
  onChange,
  fallback,
  help,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (svg: string) => void;
  /** What the preview shows while no icon is uploaded — the storefront's own default. */
  fallback: ReactNode;
  help: string;
}) {
  const { pick, error, clearError } = useSvgFilePick({ onPick: onChange });

  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-md border border-input bg-muted/40">
          {value ? <img src={svgIconDataUri(value)} alt="" className="size-6" /> : fallback}
        </div>
        <Input
          id={id}
          type="file"
          accept=".svg,image/svg+xml"
          className="h-auto py-1.5"
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Clear the input so re-picking the same file fires onChange again.
            e.target.value = "";
            void pick(file);
          }}
        />
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              clearError();
              onChange("");
            }}
          >
            Remove
          </Button>
        )}
      </div>
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : (
        <p className="text-xs text-muted-foreground">{help}</p>
      )}
    </div>
  );
}

/**
 * PNG / JPEG / WEBP / AVIF picker plus an optional URL field. SVG is not in
 * `accept` and is refused client-side; the server sniffs magic bytes too.
 */
export function RasterImageField({
  id,
  label,
  url,
  onUrlChange,
  onFile,
  uploading,
  disabled,
  uploadsAvailable,
  previewClassName,
  help,
}: {
  id: string;
  label: string;
  url: string;
  onUrlChange: (url: string) => void;
  onFile: (file: File) => void | Promise<void>;
  uploading: boolean;
  disabled: boolean;
  uploadsAvailable: boolean;
  previewClassName: string;
  help: string;
}) {
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File | undefined) => {
    setError(null);
    if (!file) return;
    try {
      await onFile(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : "the file could not be read");
    }
  };

  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-3">
        <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-input bg-muted/40">
          {url.trim() ? (
            <img src={url} alt="" className={previewClassName} />
          ) : (
            <span className="px-1 text-center text-[10px] text-muted-foreground">None</span>
          )}
        </div>
        {uploadsAvailable && (
          <Input
            id={id}
            type="file"
            accept={TENANT_ASSET_FILE_ACCEPT}
            disabled={disabled || uploading}
            className="h-auto py-1.5"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              void pick(file);
            }}
          />
        )}
      </div>
      <Input
        id={`${id}-url`}
        type="url"
        value={url}
        onChange={(e) => onUrlChange(e.target.value)}
        disabled={disabled}
        placeholder="https://"
        aria-label={`${label} URL`}
      />
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : (
        <p className="text-xs text-muted-foreground">{help}</p>
      )}
    </div>
  );
}

/**
 * The currency icon: the shared field, previewing exactly what the storefront renders while no
 * icon is uploaded — `CurrencyIcon` decides that (a glyph for a symbol-less currency, nothing for
 * one with a symbol), so the two can never disagree.
 */
export function CurrencyIconField({
  value,
  onChange,
  currency,
}: {
  value: string;
  onChange: (svg: string) => void;
  /** The currency being edited, so the fallback and the help copy match it. */
  currency: Currency;
}) {
  return (
    <SvgIconField
      id="currency-icon"
      label="Currency icon (SVG)"
      value={value}
      onChange={onChange}
      fallback={<CurrencyIcon currency={{ ...currency, iconSvg: "" }} className="size-6" />}
      help={`Optional. Shown before  on this storefront. Flat SVG shapes only — no scripts or linked files.`}
    />
  );
}
